import React, { FC, ReactNode, useCallback, useId, useState } from 'react'

import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useDraggable,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import { restrictToWindowEdges } from '@dnd-kit/modifiers'
import cn from 'clsx'
import { createPortal } from 'react-dom'

import {
  Position,
  useClampPositionOnResize
} from '../../hooks/useClampPositionOnResize'
import styles from './DevPanel.module.scss'
import { DevPanelProps, DevPanelToggleProps } from './DevPanel.types'

const stopProp = (e: React.MouseEvent) => e.stopPropagation()

export const DevPanelToggle: FC<DevPanelToggleProps> = ({
  options,
  value,
  onChange
}) => (
  <div className={styles.toggleGroup}>
    {options.map(opt => (
      <button
        key={opt.value}
        className={cn(styles.toggleBtn, {
          [styles.active]: value === opt.value
        })}
        onMouseDown={stopProp}
        onClick={() => onChange(opt.value)}
      >
        {opt.label}
      </button>
    ))}
  </div>
)

export const DevPanelMessage: FC<{ children: ReactNode }> = ({ children }) => (
  <div className={styles.message}>{children}</div>
)

type PanelInnerProps = Omit<DevPanelProps, 'defaultX' | 'defaultY'> & {
  basePosition: Position
  draggableId: string
  onPositionClamp: (pos: Position) => void
}

const PanelInner: FC<PanelInnerProps> = ({
  title,
  info,
  currentValue,
  actions,
  onClose,
  children,
  footer,
  basePosition,
  draggableId,
  onPositionClamp
}) => {
  const { attributes, listeners, setNodeRef, transform, node } = useDraggable({
    id: draggableId
  })

  useClampPositionOnResize({
    nodeRef: node,
    basePosition,
    onPositionClamp
  })

  return (
    <div
      ref={setNodeRef}
      className={styles.panel}
      style={{
        left: basePosition.x + (transform?.x ?? 0),
        top: basePosition.y + (transform?.y ?? 0)
      }}
    >
      <div className={styles.titleBar} {...listeners} {...attributes}>
        <span className={styles.titleText}>{title}</span>

        <div className={styles.titleCenter}>{currentValue}</div>

        <div className={styles.titleActions}>
          {actions}
          {info && (
            <div className={styles.infoIcon} onMouseDown={stopProp}>
              ⓘ<div className={styles.infoTooltip}>{info}</div>
            </div>
          )}
          <button
            className={styles.closeBtn}
            onMouseDown={stopProp}
            onClick={onClose}
            aria-label='Close'
          >
            ×
          </button>
        </div>
      </div>

      <div className={styles.body}>{children}</div>

      {footer && <div className={styles.footer}>{footer}</div>}
    </div>
  )
}

export const DevPanel: FC<DevPanelProps> = ({
  defaultX,
  defaultY,
  ...rest
}) => {
  const draggableId = useId()
  const [basePosition, setBasePosition] = useState<Position>({
    x: defaultX ?? window.innerWidth - 400,
    y: defaultY ?? 80
  })

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  )

  const handleDragEnd = ({ delta }: DragEndEvent) => {
    setBasePosition(prev => ({
      x: prev.x + delta.x,
      y: prev.y + delta.y
    }))
  }

  const handlePositionClamp = useCallback((pos: Position) => {
    setBasePosition(pos)
  }, [])

  return createPortal(
    <DndContext
      sensors={sensors}
      modifiers={[restrictToWindowEdges]}
      onDragEnd={handleDragEnd}
    >
      <PanelInner
        {...rest}
        basePosition={basePosition}
        draggableId={draggableId}
        onPositionClamp={handlePositionClamp}
      />
    </DndContext>,
    document.body
  )
}
